import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Clock, AlertTriangle, Brain, Loader2, CheckCircle2 } from "lucide-react";

const URGENCY = {
  overdue: { label: "OVERDUE", cls: "border-red-500 text-red-400 bg-red-500/10" },
  critical: { label: "< 2 DAYS", cls: "border-red-400 text-red-300 bg-red-400/5" },
  urgent: { label: "< 7 DAYS", cls: "border-amber-400 text-amber-300 bg-amber-400/5" },
  soon: { label: "< 14 DAYS", cls: "border-cyan-400 text-cyan-300 bg-cyan-400/5" },
  planned: { label: "< 30 DAYS", cls: "border-white/20 text-slate-300" },
  future: { label: "> 30 DAYS", cls: "border-white/10 text-slate-400" },
  no_deadline: { label: "NO DEADLINE", cls: "border-white/10 text-slate-500" },
};

const BUCKETS = ["overdue", "critical", "urgent", "soon", "planned", "future"];

function daysLabel(d) {
  if (d === null || d === undefined) return "—";
  if (d < 0) return `${Math.abs(Math.floor(d))}d overdue`;
  if (d < 1) return `${Math.floor(d * 24)}h left`;
  return `${Math.floor(d)}d left`;
}

export default function BidDue() {
  const [rows, setRows] = useState([]);
  const [bucket, setBucket] = useState("all");
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/opportunities/due").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const analyze = async (nid) => {
    setBusy(nid);
    try {
      await api.post(`/ai/bid-analysis/${nid}`);
      toast.success("Bid analysis ready");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const filtered = bucket === "all" ? rows : rows.filter(r => r.urgency === bucket);
  const counts = BUCKETS.reduce((acc, b) => ({ ...acc, [b]: rows.filter(r => r.urgency === b).length }), {});

  return (
    <div className="p-8 stagger-in" data-testid="bid-due-page">
      <div className="mb-8">
        <div className="mono-label text-cyan-400 mb-2">// DEADLINE RADAR</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Bid Due</h1>
        <p className="text-slate-400 mt-2">Every open opportunity sorted by time-to-deadline. Ready-to-review items are flagged.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <button onClick={() => setBucket("all")} className={`p-4 border transition-colors text-left ${bucket === "all" ? "border-cyan-400 bg-cyan-400/10" : "border-white/10 hover:border-white/30"}`} data-testid="bucket-all">
          <div className="mono-label text-slate-500 mb-1">ALL</div>
          <div className="font-display font-black text-2xl">{rows.length}</div>
        </button>
        {BUCKETS.map(b => (
          <button key={b} onClick={() => setBucket(b)} className={`p-4 border transition-colors text-left ${bucket === b ? "border-cyan-400 bg-cyan-400/10" : "border-white/10 hover:border-white/30"}`} data-testid={`bucket-${b}`}>
            <div className={`mono-label mb-1 ${URGENCY[b].cls.split(" ")[1]}`}>{URGENCY[b].label}</div>
            <div className="font-display font-black text-2xl">{counts[b] || 0}</div>
          </button>
        ))}
      </div>

      <div className="bg-[#0D1424] border border-white/10">
        {filtered.length === 0 && (
          <div className="p-12 text-center text-slate-500">
            <Clock className="w-10 h-10 mx-auto mb-3 text-slate-600" />
            <div>No opportunities in this bucket.</div>
          </div>
        )}
        {filtered.map(o => {
          const u = URGENCY[o.urgency];
          return (
            <div key={o.notice_id} className="p-5 border-b border-white/5 hover:bg-white/[0.02] transition-colors flex items-start gap-4" data-testid={`due-${o.notice_id}`}>
              <div className={`w-24 shrink-0 text-center border p-3 ${u.cls}`}>
                <div className="mono-label mb-1">{u.label}</div>
                <div className="font-display font-black text-xl">{daysLabel(o.days_left)}</div>
              </div>
              <Link to={`/opportunities/${o.notice_id}`} className="flex-1 min-w-0">
                <div className="mono-label text-slate-500 mb-1 flex flex-wrap gap-2">
                  <span>{o.agency}</span>
                  {o.state && <span className="text-cyan-400">{o.state}</span>}
                  {o.contract_value ? <span className="text-green-400">${Number(o.contract_value).toLocaleString()}</span> : null}
                </div>
                <div className="font-medium text-white">{o.title}</div>
                <div className="text-xs text-slate-500 mt-2 flex flex-wrap items-center gap-3">
                  <span>Deadline: {o.response_deadline ? new Date(o.response_deadline).toLocaleString() : "—"}</span>
                  {o.review_ready
                    ? <span className="text-green-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Analysis ready · ~{o.hours_to_prepare}h to submit</span>
                    : <span className="text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Not analyzed · ~{o.hours_to_prepare}h needed</span>}
                </div>
              </Link>
              <div className="shrink-0 flex flex-col items-end gap-2">
                <div className={`font-display font-black text-2xl ${o.match_score >= 85 ? "text-cyan-400" : o.match_score >= 70 ? "text-amber-400" : "text-slate-500"}`}>{o.match_score}</div>
                {!o.review_ready && (
                  <button onClick={() => analyze(o.notice_id)} disabled={busy === o.notice_id} className="mono-label text-cyan-400 hover:text-cyan-300 flex items-center gap-1 disabled:opacity-50" data-testid={`quick-analyze-${o.notice_id}`}>
                    {busy === o.notice_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Brain className="w-3 h-3" />}ANALYZE
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
